import React, { useMemo, useRef, useEffect, useContext, Fragment } from "react";
import { bool, string} from 'prop-types';
import { ExperienceContext } from "@thd-nucleus/experience-context";


export const InfiniteLoader = ({
  children,
  loadMoreItem,
  totalItemsCount,
  loadedItemsCount,
  wrapperElement = `div`,
  wrapperClassName = ''
}) => {
  const infiniteLoaderRef = useRef(null);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const paginationObserver = useRef(null);
  let isScrollListnerAdded = useRef(false);

  const disconnect = () => {
    if (paginationObserver.current && paginationObserver.current?.disconnect) {
      paginationObserver.current.disconnect();
    }
    if (isScrollListnerAdded.current) {
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('scroll', onScroll);
      isScrollListnerAdded.current = false;
    }
  };

  useEffect(() => {
    return () => { disconnect(); };
  }, []);

  const observe = (elem, cb, height) => {
    const observerOptions = {
      rootMargin: `0px 0px ${height}px 0px`
    };
    paginationObserver.current = new IntersectionObserver(async (_ref2, obs) => {
      let [entry] = _ref2;
      if (!entry.isIntersecting) return;
      disconnect();
      cb();
    }, observerOptions);
    paginationObserver.current.observe(elem);
  };

  const loadMore = () => {
    if (loadedItemsCount > 0 && loadedItemsCount < totalItemsCount) {
      loadMoreItem();
    }
  };

  const onScroll = (event) => {
    // this is for checking if the user reaches the bottom of the view port.
    // this act as a fail safe, if the user is scrolling too fast and the observing element
    // for intersection is already in the view port
    let documentHeight = document.body.scrollHeight;
    let currentScroll = window.scrollY + window.innerHeight;
    let modifier = isMobile ? 150 : 400;
    if (currentScroll + modifier > documentHeight) {
      const observerTrack = paginationObserver.current?.takeRecords ? paginationObserver.current.takeRecords() : [];
      if (observerTrack?.length === 0) {
        disconnect();
        loadMore();
      }
    }
  };

  useEffect(() => {
    if (loadedItemsCount > 0 && loadedItemsCount < totalItemsCount) {
      const allItems = infiniteLoaderRef.current?.children;
      const totalItems = allItems?.length;
      if (totalItems > 0) {
        const lastChild = infiniteLoaderRef.current?.lastElementChild;
        const height = lastChild?.offsetHeight || 60;
        disconnect();
        observe(lastChild, loadMore, height);
      }
      if (!isScrollListnerAdded.current) {
        isScrollListnerAdded.current = true;
        document.addEventListener('scroll', onScroll);
      }
    }
  }, [loadedItemsCount]);

  const Wrapper = wrapperElement || `div`;
  return (
    <Wrapper className={wrapperClassName} ref={infiniteLoaderRef}>
      {
        children
      }
    </Wrapper>
  );
}

InfiniteLoader.propTypes = {
  wrapperClassName: string
};

InfiniteLoader.defaultProps = {
  wrapperClassName: ''
};
